// Content sanitization utilities for security hardening

/**
 * Sanitizes HTML content by removing potentially dangerous elements and attributes
 * Used to mitigate XSS risks in dangerouslySetInnerHTML usage
 */
export const sanitizeHTML = (html: string): string => {
  // Create a temporary div element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;

  // Define allowed tags and attributes
  const allowedTags = [
    'div', 'span', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'strong', 'em', 'b', 'i', 'u', 'a', 'img', 'br', 'hr',
    'ul', 'ol', 'li', 'table', 'thead', 'tbody', 'tr', 'td', 'th'
  ];

  const allowedAttributes: Record<string, string[]> = {
    'a': ['href', 'title', 'target'],
    'img': ['src', 'alt', 'width', 'height', 'title'],
    'div': ['class', 'id', 'style'],
    'span': ['class', 'id', 'style'],
    'p': ['class', 'id', 'style'],
    'table': ['class', 'id'],
    'td': ['class', 'colspan', 'rowspan'],
    'th': ['class', 'colspan', 'rowspan']
  };

  // Remove dangerous scripts and potentially harmful content
  const scripts = tempDiv.querySelectorAll('script, style, iframe, object, embed');
  scripts.forEach(script => script.remove());

  // Sanitize remaining elements
  const allElements = tempDiv.querySelectorAll('*');
  allElements.forEach(element => {
    const tagName = element.tagName.toLowerCase();
    
    // Remove disallowed tags
    if (!allowedTags.includes(tagName)) {
      element.remove();
      return;
    }

    // Remove dangerous attributes
    const attributes = Array.from(element.attributes);
    attributes.forEach(attr => {
      const attrName = attr.name.toLowerCase();
      const allowedAttrs = allowedAttributes[tagName] || [];
      
      // Remove dangerous event handlers and forbidden attributes
      if (
        attrName.startsWith('on') || // Event handlers (onclick, onload, etc.)
        attrName === 'javascript:' ||
        attrName === 'vbscript:' ||
        !allowedAttrs.includes(attrName)
      ) {
        element.removeAttribute(attr.name);
      }
    });

    // Sanitize href attributes to prevent javascript: schemes
    if (element instanceof HTMLAnchorElement && element.href) {
      const href = element.getAttribute('href');
      if (href && (href.includes('javascript:') || href.includes('vbscript:'))) {
        element.removeAttribute('href');
      }
    }
  });

  return tempDiv.innerHTML;
};

/**
 * Validates JSON-LD structured data for security
 * Ensures structured data doesn't contain executable code
 */
export const sanitizeStructuredData = (data: any): any => {
  if (typeof data === 'string') {
    // Remove any script-like content from strings
    return data.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  }
  
  if (Array.isArray(data)) {
    return data.map(item => sanitizeStructuredData(item));
  }
  
  if (data && typeof data === 'object') {
    const sanitized: any = {};
    for (const [key, value] of Object.entries(data)) {
      // Skip potentially dangerous keys
      if (key.toLowerCase().includes('script') || key.toLowerCase().includes('eval')) {
        continue;
      }
      sanitized[key] = sanitizeStructuredData(value);
    }
    return sanitized;
  }
  
  return data;
};

/**
 * Creates safe inline styles by filtering out dangerous CSS properties
 */
export const sanitizeInlineStyles = (styles: string): string => {
  // Remove dangerous CSS properties that could be used for attacks
  const dangerousProperties = [
    'javascript:', 'expression(', 'behavior:', 'binding:', '@import',
    'moz-binding', '-moz-binding', 'behaviour', 'behavior'
  ];
  
  let sanitized = styles;
  dangerousProperties.forEach(dangerous => {
    const regex = new RegExp(dangerous.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi');
    sanitized = sanitized.replace(regex, '');
  });
  
  return sanitized;
};

/**
 * Validates email template content for security
 */
export const validateEmailContent = (content: string): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  // Check for script tags
  if (/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi.test(content)) {
    errors.push('Script tags are not allowed in email content');
  }
  
  // Check for event handlers
  if (/on\w+\s*=/gi.test(content)) {
    errors.push('Event handlers (onclick, onload, etc.) are not allowed');
  }
  
  // Check for javascript: schemes
  if (/javascript:/gi.test(content)) {
    errors.push('JavaScript schemes are not allowed');
  }
  
  // Check for form elements (potential for CSRF)
  if (/<(form|input|button|textarea|select)\b/gi.test(content)) {
    errors.push('Form elements should be avoided in email templates');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};